import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Admin Dashboard Home
export const AdminDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'admin';
  const isModerator = user?.role === 'moderator';
  
  console.log('👤 AdminDashboard - User:', user?.email, 'Role:', user?.role, 'isAdmin:', isAdmin);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('📊 Fetching stats with token:', !!token);
      
      const statsRes = await axios.get(`${API}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ Stats fetched:', statsRes.data);
      
      setStats(statsRes.data);
      console.log('✅ Stats set successfully');
    } catch (error) {
      console.error('❌ Error fetching stats:', error);
      // Set default stats on error
      setStats({
        total_posts: 0,
        total_users: 0,
        pending_comments: 0,
        total_comments: 0,
        usage_stats: {
          total_pdf_generations: 0,
          total_premium_views: 0,
          recent_activity: []
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-800">
            {isAdmin ? 'Admin Dashboard' : 'Moderator Dashboard'}
          </h1>
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
            isAdmin ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {isAdmin ? '🔑 Administrator' : '👮 Moderator'}
          </span>
        </div>
        
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <StatCard title="Total Users" value={stats?.total_users || 0} icon="👥" color="green" />
          <StatCard title="PDF Generations" value={stats?.usage_stats?.total_pdf_generations || 0} icon="📄" color="red" />
          <StatCard title="Premium Views" value={stats?.usage_stats?.total_premium_views || 0} icon="👁️" color="yellow" />
          <StatCard title="Pending Comments" value={stats?.pending_comments || 0} icon="💬" color="orange" />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className={`grid ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {isAdmin && (
              <Link to="/admin/posts/new" className="p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 text-center font-medium">
                ➕ New Blog Post
              </Link>
            )}
            <Link to="/admin/comments" className="p-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-center font-medium">
              💬 Moderate Comments ({stats?.pending_comments || 0})
            </Link>
            {isAdmin && (
              <Link to="/admin/backup" className="p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-center font-medium">
                💾 Backup Database
              </Link>
            )}
          </div>
        </div>

        {/* Moderator Info Banner */}
        {isModerator && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
            <p className="text-blue-800">
              <strong>👮 Moderator Access:</strong> You can moderate comments and view users. Contact admin for additional permissions.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="grid md:grid-cols-2 gap-6">
          <NavCard title="Manage Posts" description={isAdmin ? "View, edit, and delete blog posts" : "View blog posts"} link="/admin/posts" icon="📄" />
          <NavCard title="Manage Pages" description={isAdmin ? "Create and manage custom website pages" : "View pages"} link="/admin/pages" icon="📑" />
          <NavCard title="View Users" description={isAdmin ? "View users and change roles" : "View registered users"} link="/admin/users" icon="👤" />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  const colors = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    purple: 'bg-purple-500'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`${colors[color]} w-12 h-12 rounded-full flex items-center justify-center text-2xl`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const NavCard = ({ title, description, link, icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <div className="flex items-start space-x-4">
      <div className="text-4xl">{icon}</div>
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-1">{title}</h3>
        <p className="text-gray-600 text-sm">{description}</p>
      </div>
    </div>
  </Link>
);

// Manage Posts
export const ManagePosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('📝 Fetching posts from:', `${API}/blog/posts`);
      const response = await axios.get(`${API}/blog/posts?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ Posts fetched:', response.data.length);
      setPosts(response.data);
    } catch (error) {
      console.error('❌ Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/admin/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      alert('Post deleted successfully');
    } catch (error) {
      alert('Error deleting post');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-800">Manage Posts</h1>
          <Link to="/admin/posts/new" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">
            ➕ New Post
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map(post => (
                <tr key={post.id}>
                  <td className="px-6 py-4">
                    <Link to={`/blog/${post.slug}`} className="text-blue-600 hover:underline" target="_blank">
                      {post.title}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{post.author}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(post.published_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{post.comments?.length || 0}</td>
                  <td className="px-6 py-4">
                    <Link to={`/admin/posts/edit/${post.id}`} className="text-blue-600 hover:underline mr-4">
                      Edit
                    </Link>
                    <button onClick={() => deletePost(post.id)} className="text-red-600 hover:underline">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Create/Edit Post
export const PostEditor = ({ postId }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (postId) {
      fetchPost();
    }
  }, [postId]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/post/${postId}`);
      const post = response.data;
      setTitle(post.title);
      setContent(post.content);
      setExcerpt(post.excerpt || '');
      setCategory(post.categories[0] || '');
    } catch (error) {
      alert('Error loading post');
    } finally {
      setLoading(false);
    }
  };

  const savePost = async () => {
    if (!title || !content) {
      alert('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      if (postId) {
        await axios.put(`${API}/admin/posts/${postId}`, {
          title, content, excerpt, category
        });
        alert('Post updated successfully');
      } else {
        const response = await axios.post(`${API}/admin/posts`, {
          title, content, excerpt, category, published: true
        });
        alert('Post created successfully');
        window.location.href = '/admin/posts';
      }
    } catch (error) {
      alert('Error saving post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-800">
            {postId ? 'Edit Post' : 'Create New Post'}
          </h1>
          <Link to="/admin/posts" className="text-blue-600 hover:underline">
            ← Back to Posts
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Enter post title..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt (Optional)</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Short description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category (Optional)</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Vaideekam, Recipes, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows="20"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
              placeholder="Write your content here... (HTML supported)"
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={savePost}
              disabled={saving}
              className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-medium"
            >
              {saving ? 'Saving...' : (postId ? 'Update Post' : 'Publish Post')}
            </button>
            <Link
              to="/admin/posts"
              className="bg-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-400 font-medium"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Manage Pages
export const ManagePages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/pages/${pageId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPages(pages.filter(p => p.id !== pageId));
      alert('Page deleted successfully');
    } catch (error) {
      alert('Error deleting page');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-800">Manage Pages</h1>
          <Link to="/admin/pages/new" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">
            ➕ New Page
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pages.map(page => (
                <tr key={page.id}>
                  <td className="px-6 py-4">
                    <Link to={`/page/${page.slug}`} className="text-blue-600 hover:underline" target="_blank">
                      {page.title}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{page.author}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(page.published_at || page.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <Link to={`/admin/pages/edit/${page.id}`} className="text-blue-600 hover:underline mr-4">
                      Edit
                    </Link>
                    <button onClick={() => deletePage(page.id)} className="text-red-600 hover:underline">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Create/Edit Page
export const PageEditor = ({ pageId }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pageId) {
      fetchPage();
    }
  }, [pageId]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/page/${pageId}`);
      const page = response.data;
      setTitle(page.title);
      setContent(page.content);
    } catch (error) {
      alert('Error loading page');
    } finally {
      setLoading(false);
    }
  };

  const savePage = async () => {
    if (!title || !content) {
      alert('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (pageId) {
        await axios.put(`${API}/admin/pages/${pageId}`, {
          title, content
        }, { headers });
        alert('Page updated successfully');
      } else {
        await axios.post(`${API}/admin/pages`, {
          title, content, published: true
        }, { headers });
        alert('Page created successfully');
        window.location.href = '/admin/pages';
      }
    } catch (error) {
      alert('Error saving page');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-800">
            {pageId ? 'Edit Page' : 'Create New Page'}
          </h1>
          <Link to="/admin/pages" className="text-blue-600 hover:underline">
            ← Back to Pages
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="Enter page title..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows="20"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
              placeholder="Write your content here... (HTML supported)"
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={savePage}
              disabled={saving}
              className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 font-medium"
            >
              {saving ? 'Saving...' : (pageId ? 'Update Page' : 'Publish Page')}
            </button>
            <Link
              to="/admin/pages"
              className="bg-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-400 font-medium"
            >
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Comment Moderation
export const ModerateComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/admin/comments/pending`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (commentId, approved) => {
    try {
      await axios.patch(`${API}/admin/comments/${commentId}/approve`, null, {
        params: { approved }
      });
      setComments(comments.filter(c => c.id !== commentId));
      alert(`Comment ${approved ? 'approved' : 'rejected'}`);
    } catch (error) {
      alert('Error processing comment');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axios.delete(`${API}/admin/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      alert('Comment deleted');
    } catch (error) {
      alert('Error deleting comment');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-green-800 mb-8">
          Moderate Comments ({comments.length} pending)
        </h1>

        {comments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No pending comments to moderate
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map(comment => (
              <div key={comment.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-semibold text-gray-800">{comment.user_name}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(comment.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-blue-600 mt-1">Page: {comment.page_slug}</p>
                  </div>
                </div>
                <div className="mb-4 p-4 bg-gray-50 rounded" dangerouslySetInnerHTML={{ __html: comment.content }} />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleComment(comment.id, true)}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={() => handleComment(comment.id, false)}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700"
                  >
                    ✗ Reject
                  </button>
                  <button
                    onClick={() => deleteComment(comment.id)}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Backup System
export const BackupSystem = () => {
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const createBackup = async () => {
    setCreating(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/backup`, {
        collections: ['blog_posts', 'blog_pages', 'blog_categories', 'blog_tags', 'users', 'comments']
      });
      setResult(response.data);
      alert('Backup created successfully!');
    } catch (error) {
      alert('Error creating backup');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-green-800 mb-8">Database Backup</h1>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Create Manual Backup</h2>
          <p className="text-gray-600 mb-6">
            This will create a backup of all your blog posts, pages, comments, users, categories, and tags.
          </p>

          <button
            onClick={createBackup}
            disabled={creating}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
          >
            {creating ? 'Creating Backup...' : '💾 Create Backup Now'}
          </button>

          {result && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2">Backup Created Successfully!</h3>
              <p className="text-sm text-gray-700">Filename: {result.filename}</p>
              <p className="text-sm text-gray-700">Total Documents: {result.total_documents}</p>
              <p className="text-sm text-gray-700">Collections: {result.collections.join(', ')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// User Management
export const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get user from localStorage directly
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  console.log('👤 ManageUsers - Current user:', storedUser.email, 'Role:', storedUser.role);

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCurrentUser(response.data);
      console.log('✅ Current user fetched from API:', response.data.role);
    } catch (error) {
      console.error('❌ Error fetching current user:', error);
      // Fallback to localStorage
      setCurrentUser(storedUser);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async (userId, newRole) => {
    if (currentUser?.role !== 'admin') {
      alert('Only administrators can change user roles');
      return;
    }
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      alert('User role updated');
    } catch (error) {
      alert('Error updating role');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  // Check admin status from currentUser or fallback to storedUser
  const isAdmin = (currentUser?.role === 'admin') || (storedUser?.role === 'admin');
  console.log('🔑 isAdmin check:', isAdmin, 'currentUser:', currentUser?.role, 'storedUser:', storedUser?.role);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-green-800 mb-8">
          {isAdmin ? 'Manage Users' : 'View Users'} ({users.length})
        </h1>
        
        {!isAdmin && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
            <p className="text-yellow-800">
              <strong>ℹ️ Moderator View:</strong> You can view users but cannot change roles. Contact administrator for assistance.
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registration Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[...users]
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                .map((user, idx) => (
                  <tr key={user.id || idx} className={idx === 0 ? "bg-green-50/50" : ""}>
                    <td className="px-4 py-4 text-xs font-bold text-gray-400">
                      #{idx + 1}
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-900">{user.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'moderator' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                      {user.created_at ? new Date(user.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      {isAdmin ? (
                        <select
                          value={user.role}
                          onChange={(e) => changeRole(user.id, e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value="user">User</option>
                          <option value="moderator">Moderator</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className="text-gray-500 text-sm">View Only</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
